import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ReactFlow, Background, Controls, Handle, Position, MarkerType,
  type Edge, type Node, type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import {
  Save, PlusCircle, Camera, ShieldCheck, Flag, IndianRupee, Replace,
  HelpCircle, MessageCircle, Sparkles, Eye, ScanText, Calculator,
  Brain, MessageSquare, GitBranch, MailCheck, Workflow, Maximize2, X,
} from "lucide-react";
import { cls } from "../../lib/cls";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import { env } from "../../lib/env";
import { formatCompactMoney } from "../../lib/i18n/format";
import { ageDays, daysUntil } from "../../lib/temporal";

/* AgenticContextFlow — the one diagram the designer (or their client)
 * reads to understand what the agent does *for them*.
 *
 *   what you did / asked  →   which agents picked it up   →   what came back
 *
 * Click a trigger on the left to light up its chain through the
 * specialists into the outputs. No model names, no framework names,
 * no prose preamble — the diagram IS the explanation. */

type Audience = "designer" | "client";
type AgentId = "watch" | "ingest" | "constraint" | "substitution" | "narrator" | "memory" | "project";
type OutputId = "alert" | "suggestion" | "why" | "email" | "approval" | "promote" | "schedule";

interface Trigger {
  id: string;
  audience: Audience | "both";
  kind: "action" | "question";
  label: string;
  icon: JSX.Element;
  /** Which agents this trigger lights up, in firing order. */
  agents: AgentId[];
  /** Which outputs this trigger eventually produces. */
  outputs: OutputId[];
  /** One-line example of what comes back. Read by the lit Outputs row. */
  example: Record<OutputId, string> | Partial<Record<OutputId, string>>;
}

const A = (id: AgentId, label: string, icon: JSX.Element): { id: AgentId; label: string; icon: JSX.Element } =>
  ({ id, label, icon });

const AGENTS = [
  A("watch",        "Watch",          <Eye           className="w-3.5 h-3.5" />),
  A("ingest",       "Ingest",         <ScanText      className="w-3.5 h-3.5" />),
  A("constraint",   "Constraint",     <Calculator    className="w-3.5 h-3.5" />),
  A("substitution", "Substitute",     <Replace       className="w-3.5 h-3.5" />),
  A("narrator",     "Narrator",       <MessageSquare className="w-3.5 h-3.5" />),
  A("memory",       "Memory",         <Brain         className="w-3.5 h-3.5" />),
  A("project",      "Plan & tasks",   <GitBranch     className="w-3.5 h-3.5" />),
] as const;

const OUTPUTS: { id: OutputId; label: string; icon: JSX.Element; tone: "warn" | "info" | "ok" }[] = [
  { id: "alert",      label: "Alert",                  icon: <Flag           className="w-3.5 h-3.5" />, tone: "warn" },
  { id: "suggestion", label: "Substitute proposed",    icon: <Replace        className="w-3.5 h-3.5" />, tone: "info" },
  { id: "why",        label: "Why · plain English",    icon: <MessageSquare  className="w-3.5 h-3.5" />, tone: "info" },
  { id: "email",      label: "Client email · drafted", icon: <MailCheck      className="w-3.5 h-3.5" />, tone: "ok"   },
  { id: "approval",   label: "Approval · nudged",      icon: <ShieldCheck    className="w-3.5 h-3.5" />, tone: "info" },
  { id: "promote",    label: "Memory · promoted",      icon: <Sparkles       className="w-3.5 h-3.5" />, tone: "ok"   },
  { id: "schedule",   label: "Schedule · re-planned",  icon: <Workflow       className="w-3.5 h-3.5" />, tone: "info" },
];

/* CLAUDE.md §1 — every example is derived from the live project
 * graph, not literal copy. The Trigger array still encodes the
 * agentic *topology* (which trigger lights which agents); the
 * example strings come from the typed entities on the current
 * project. Change the project → the examples change. */
// Names preserved for callers; both render via the country-aware compact formatter.
function inrK(n: number): string { return formatCompactMoney(n); }
function inrL(n: number): string { return formatCompactMoney(n); }

function buildExamples(): {
  alertLine: string;
  whyLine: string;
  suggestionLine: string;
  approvalLine: string;
  scheduleLine: string;
  emailLine: string;
  promoteLine: string;
  swapWhy: string;
  budgetSuggestion: string;
  budgetWhy: string;
  budgetEmail: string;
  fixAlert: string;
  fixWhy: string;
  staleApproval: string;
  staleWhy: string;
  budgetAlert: string;
  msAlert: string;
  msSchedule: string;
  renderAlert: string;
  renderWhy: string;
} {
  const nodes = getActiveProjectGraph().nodes;
  const alerts = nodes.filter((n) => n.kind === "Alert");
  const warnAlert = alerts.find((a) => a.attrs["severity"] === "warn") ?? alerts[0];
  const longLeadSku = nodes
    .filter((n) => n.kind === "SKU" && typeof n.attrs["leadDays"] === "number")
    .sort((a, b) => (b.attrs["leadDays"] as number) - (a.attrs["leadDays"] as number))[0];
  const substituteSku = nodes.find((n) =>
    n.kind === "SKU" && typeof n.attrs["source"] === "string" && (n.attrs["source"] as string).includes("substitute"),
  );
  const lightMs = nodes.find((n) => n.kind === "Milestone" && /light|lighting/i.test(n.label));
  const kitchenMs = nodes.find((n) => n.kind === "Milestone" && /kitchen|civil|hand-off/i.test(n.label));
  const handover  = nodes.find((n) => n.kind === "Milestone" && /handover/i.test(n.label));
  const pending = nodes.find((n) => n.kind === "Approval" && n.attrs["state"] === "pending");
  const project = nodes.find((n) => n.kind === "Project");
  const budgetLines = nodes.filter((n) => n.kind === "BudgetLine");

  const longLeadLabel = longLeadSku?.label ?? "the long-lead item";
  const longLeadDays  = (longLeadSku?.attrs["leadDays"] as number | undefined) ?? 0;
  const subLeadDays   = (substituteSku?.attrs["leadDays"] as number | undefined) ?? 0;
  const subStyle      = (substituteSku?.attrs["styleMatch"] as number | undefined) ?? 0;
  const longLeadPrice = (longLeadSku?.attrs["price"] as number | undefined) ?? 0;
  const subPrice      = (substituteSku?.attrs["price"] as number | undefined) ?? 0;
  const priceDelta    = subPrice - longLeadPrice;

  const budgetCap = (project?.attrs["budgetCap"] as number | undefined) ?? 0;
  const committed = budgetLines.reduce((a, l) => a + (typeof l.attrs["amount"] === "number" ? (l.attrs["amount"] as number) : 0), 0);
  const overshoot = committed - budgetCap;
  const pendingAge = pending ? ageDays(pending) : 0;

  return {
    alertLine: warnAlert ? `${warnAlert.label}.` : "No alerts on this project.",
    whyLine: longLeadSku && lightMs
      ? `${longLeadLabel} needs ${longLeadDays} d · risks ${lightMs.label} in ${daysUntil(lightMs)} d.`
      : "No risk paths to explain right now.",
    suggestionLine: substituteSku
      ? `${substituteSku.label} · ${subLeadDays} d · ${Math.round(subStyle * 100)}% match · Δ ${priceDelta >= 0 ? "+" : ""}${inrK(priceDelta)}.`
      : "No substitute proposed.",
    approvalLine: pending
      ? `${pending.label} · ${pendingAge} d pending · client nudged.`
      : "No approvals pending.",
    scheduleLine: kitchenMs && handover
      ? `Critical path: ${kitchenMs.label} → ${handover.label}.`
      : "Schedule reads stable.",
    emailLine: substituteSku
      ? `Drafted note: swap to ${substituteSku.label} · keeps your install date.`
      : "Drafted client note based on the project graph.",
    promoteLine: `Promoted: after ${env.VITE_MEMORY_MIN_ACCEPTS} accepts (max ${Math.round(env.VITE_MEMORY_MAX_REJECT_RATIO * 100)}% rejects).`,
    swapWhy: longLeadSku && substituteSku && lightMs
      ? `${longLeadSku.label} needed ${longLeadDays} d. ${substituteSku.label} keeps ${lightMs.label} on track and matches your style ${Math.round(subStyle * 100)}%.`
      : "Substitution explained from the project graph.",
    budgetSuggestion: substituteSku && priceDelta < 0
      ? `Swap to ${substituteSku.label} → ${inrK(priceDelta)}.`
      : "Substitution candidate ranked from catalog.",
    budgetWhy: "Style impact stays within tolerance · Master & Living untouched.",
    budgetEmail: pending ? `Drafted client note: budget rebalance proposal.` : "Drafted note from the project graph.",
    fixAlert: warnAlert ? warnAlert.label : "All cohesion checks pass.",
    fixWhy: lightMs && longLeadSku
      ? `${lightMs.label} drives the deadline · address ${longLeadSku.label} first.`
      : "Project is clean for render.",
    staleApproval: pending ? `${pending.label} · ${pendingAge} d with client.` : "No stale approvals.",
    staleWhy: `Approval SLA · ${env.VITE_APPROVAL_AGE_DAYS} d.`,
    budgetAlert: budgetCap > 0 && overshoot > 0
      ? `Budget ${Math.round((committed / budgetCap) * 100)}% committed — over cap by ${inrK(overshoot)}.`
      : budgetCap > 0
        ? `Budget ${Math.round((committed / budgetCap) * 100)}% of ${inrL(budgetCap)} committed.`
        : "Budget watched.",
    msAlert: handover && (handover.attrs["slackDays"] as number) <= 0
      ? `${handover.label} slack now 0 d.`
      : "Milestone slack OK.",
    msSchedule: kitchenMs ? `${kitchenMs.label} pulled in to absorb risk.` : "Schedule re-balanced.",
    renderAlert: warnAlert ? warnAlert.label : "Cohesion within tolerance.",
    renderWhy: longLeadSku ? `Address ${longLeadSku.label} before next render.` : "Project is clean for render.",
  };
}

const ex = buildExamples();

const TRIGGERS: Trigger[] = [
  // ── Designer ACTIONS ──────────────────────────────────────────
  { id: "save",       audience: "designer", kind: "action",   label: "You save",                icon: <Save        className="w-3.5 h-3.5" />,
    agents: ["watch", "ingest", "constraint", "narrator"],
    outputs: ["alert", "why"],
    example: { alert: ex.alertLine, why: ex.whyLine } },
  { id: "addItem",    audience: "designer", kind: "action",   label: "You add an item",         icon: <PlusCircle  className="w-3.5 h-3.5" />,
    agents: ["watch", "ingest", "constraint", "substitution"],
    outputs: ["alert", "suggestion"],
    example: { alert: ex.alertLine, suggestion: ex.suggestionLine } },
  { id: "render",     audience: "designer", kind: "action",   label: "You click Take render",   icon: <Camera      className="w-3.5 h-3.5" />,
    agents: ["watch", "constraint", "narrator"],
    outputs: ["alert", "why"],
    example: { alert: ex.renderAlert, why: ex.renderWhy } },
  { id: "approval",   audience: "designer", kind: "action",   label: "Approval updates",        icon: <ShieldCheck className="w-3.5 h-3.5" />,
    agents: ["watch", "constraint", "narrator", "memory"],
    outputs: ["approval", "promote"],
    example: { approval: ex.approvalLine, promote: ex.promoteLine } },
  { id: "milestone",  audience: "designer", kind: "action",   label: "Milestone shifts",        icon: <Flag        className="w-3.5 h-3.5" />,
    agents: ["watch", "constraint", "project", "narrator"],
    outputs: ["alert", "schedule"],
    example: { alert: ex.msAlert, schedule: ex.msSchedule } },
  { id: "budget",     audience: "designer", kind: "action",   label: "Budget cap moves",        icon: <IndianRupee className="w-3.5 h-3.5" />,
    agents: ["watch", "constraint", "substitution", "narrator"],
    outputs: ["alert", "suggestion", "why"],
    example: { alert: ex.budgetAlert, suggestion: ex.budgetSuggestion, why: ex.budgetWhy } },
  // ── Designer QUESTIONS ─────────────────────────────────────────
  { id: "qRisk",      audience: "designer", kind: "question", label: "Why is this at risk?",    icon: <HelpCircle  className="w-3.5 h-3.5" />,
    agents: ["narrator"],
    outputs: ["why"],
    example: { why: ex.whyLine } },
  { id: "qFix",       audience: "designer", kind: "question", label: "Anything to fix before I render?", icon: <HelpCircle className="w-3.5 h-3.5" />,
    agents: ["constraint", "narrator"],
    outputs: ["alert", "why"],
    example: { alert: ex.fixAlert, why: ex.fixWhy } },
  { id: "qStale",     audience: "designer", kind: "question", label: "Any approvals going stale?",   icon: <HelpCircle  className="w-3.5 h-3.5" />,
    agents: ["constraint", "narrator", "memory"],
    outputs: ["approval", "why"],
    example: { approval: ex.staleApproval, why: ex.staleWhy } },
  // ── Client QUESTIONS ───────────────────────────────────────────
  { id: "qHandover",  audience: "client",   kind: "question", label: "When will we get the keys?",   icon: <MessageCircle className="w-3.5 h-3.5" />,
    agents: ["project", "narrator"],
    outputs: ["why", "schedule"],
    example: { why: ex.whyLine, schedule: ex.scheduleLine } },
  { id: "qSwap",      audience: "client",   kind: "question", label: "Why did you swap the pendant?", icon: <MessageCircle className="w-3.5 h-3.5" />,
    agents: ["narrator", "memory"],
    outputs: ["why"],
    example: { why: ex.swapWhy } },
  { id: "qBudget",    audience: "client",   kind: "question", label: "Can we trim spend?",             icon: <MessageCircle className="w-3.5 h-3.5" />,
    agents: ["constraint", "substitution", "narrator", "memory"],
    outputs: ["suggestion", "why", "email"],
    example: { suggestion: ex.budgetSuggestion, why: ex.budgetWhy, email: ex.budgetEmail } },
];

const TONE_OUTPUT: Record<"warn" | "info" | "ok", string> = {
  warn: "border-foyr/40 bg-foyr/10 text-foyr-deep",
  info: "border-sky-200 bg-sky-50 text-sky-800",
  ok:   "border-fern/30 bg-fern/10 text-fern",
};

/* ─── Node renderers ──────────────────────────────────────────── */

interface EntryData   extends Record<string, unknown> { label: string; kind: "action" | "question"; icon: JSX.Element; }
interface AgentData   extends Record<string, unknown> { id: AgentId;  label: string; icon: JSX.Element; lit: boolean; }
interface OutputData  extends Record<string, unknown> { id: OutputId; label: string; icon: JSX.Element; tone: "warn" | "info" | "ok"; lit: boolean; example?: string; }

/** Entry pill on the right pane — anchors the agent fan-out to the
 * trigger the designer (or client) selected on the left. */
function EntryNode({ data }: { data: EntryData }): JSX.Element {
  return (
    <div className="rounded-xl border border-ink-950 bg-ink-950 text-cream px-3 py-2 flex items-center gap-2 shadow-md min-w-[160px]">
      <Handle type="source" position={Position.Right} className="!w-1.5 !h-1.5 !bg-foyr !border-foyr" />
      <span className="text-foyr">{data.icon}</span>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wider font-mono opacity-60">
          {data.kind === "action" ? "Action" : "Question"}
        </div>
        <div className="text-[12px] font-semibold leading-tight truncate">{data.label}</div>
      </div>
    </div>
  );
}

function AgentNode({ data }: { data: AgentData }): JSX.Element {
  return (
    <div className={cls(
      "rounded-xl border px-3 py-2 flex items-center gap-2 transition-all min-w-[150px]",
      data.lit
        ? "bg-foyr/10 border-foyr/40 text-foyr-deep shadow-sm"
        : "bg-cream border-border text-ink-500",
    )}>
      <Handle type="target" position={Position.Left}  className="!w-1.5 !h-1.5 !bg-ink-300 !border-ink-300" />
      <Handle type="source" position={Position.Right} className="!w-1.5 !h-1.5 !bg-ink-300 !border-ink-300" />
      <span className={data.lit ? "text-foyr" : "text-ink-400"}>{data.icon}</span>
      <span className="text-[12.5px] font-semibold leading-tight truncate">{data.label}</span>
      {data.lit ? <span className="ml-auto w-1.5 h-1.5 rounded-full bg-foyr animate-pulse" /> : null}
    </div>
  );
}

function OutputNode({ data }: { data: OutputData }): JSX.Element {
  return (
    <div
      className={cls(
        "rounded-xl border px-3.5 py-2.5 transition-all",
        data.lit ? TONE_OUTPUT[data.tone] : "bg-cream border-border text-ink-400",
      )}
      style={{ width: OUTPUT_W_PX }}
    >
      <Handle type="target" position={Position.Left} className="!w-1.5 !h-1.5 !bg-ink-300 !border-ink-300" />
      <div className="flex items-center gap-2">
        <span className={data.lit ? "" : "text-ink-300"}>{data.icon}</span>
        <span className="text-[12.5px] font-semibold leading-tight">{data.label}</span>
      </div>
      {data.lit && data.example ? (
        <div className="text-[11px] mt-1.5 leading-snug opacity-90 italic break-words">{data.example}</div>
      ) : null}
    </div>
  );
}

const NODE_TYPES: NodeTypes = {
  entry:  EntryNode  as unknown as NodeTypes[string],
  agent:  AgentNode  as unknown as NodeTypes[string],
  output: OutputNode as unknown as NodeTypes[string],
};

/* ─── Right-pane layout (agents + outputs only, in xyflow) ──────── */
/* Triggers live OUTSIDE xyflow as a regular DOM list so clicks are
 * native HTML events (xyflow's panOnDrag was swallowing them).  The
 * xyflow canvas only renders the *consequence*: which specialists
 * lit up + which outputs they produced for the selected trigger. */

const ENTRY_NODE_ID = "entry";

/* Single source of truth for output card width so dagre's bbox and
 * the rendered card stay in lockstep — otherwise the card overflows
 * its bbox and gets clipped by the xyflow canvas. */
const OUTPUT_W_PX  = 300;
const OUTPUT_H_PX  = 96;
const ENTRY_W_PX   = 180;
const AGENT_W_PX   = 170;

function layoutRight(selected: Trigger | undefined): { nodes: Node[]; edges: Edge[] } {
  if (!selected) return { nodes: [], edges: [] };

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 16, ranksep: 90, marginx: 18, marginy: 18 });

  const visibleAgents = AGENTS.filter((a) => selected.agents.includes(a.id));
  const visibleOuts   = OUTPUTS.filter((o) => selected.outputs.includes(o.id));

  g.setNode(ENTRY_NODE_ID, { width: ENTRY_W_PX, height: 48 });
  for (const a of visibleAgents) g.setNode(`a-${a.id}`, { width: AGENT_W_PX, height: 46 });
  for (const o of visibleOuts)   g.setNode(`o-${o.id}`, { width: OUTPUT_W_PX, height: OUTPUT_H_PX });

  for (const a of selected.agents) g.setEdge(ENTRY_NODE_ID, `a-${a}`);
  for (const a of selected.agents) {
    for (const o of selected.outputs) g.setEdge(`a-${a}`, `o-${o}`);
  }

  dagre.layout(g);

  const nodes: Node[] = [];

  const ep = g.node(ENTRY_NODE_ID);
  nodes.push({
    id: ENTRY_NODE_ID,
    type: "entry",
    position: { x: ep.x - ENTRY_W_PX / 2, y: ep.y - 24 },
    data: { label: selected.label, kind: selected.kind, icon: selected.icon } as EntryData,
    draggable: false, selectable: false,
  });

  for (const a of visibleAgents) {
    const p = g.node(`a-${a.id}`);
    nodes.push({
      id: `a-${a.id}`,
      type: "agent",
      position: { x: p.x - AGENT_W_PX / 2, y: p.y - 23 },
      data: { id: a.id, label: a.label, icon: a.icon, lit: true } as AgentData,
      draggable: false, selectable: false,
    });
  }
  for (const o of visibleOuts) {
    const p = g.node(`o-${o.id}`);
    nodes.push({
      id: `o-${o.id}`,
      type: "output",
      position: { x: p.x - OUTPUT_W_PX / 2, y: p.y - OUTPUT_H_PX / 2 },
      data: {
        id: o.id, label: o.label, icon: o.icon, tone: o.tone,
        lit: true,
        example: (selected.example as Partial<Record<OutputId, string>>)[o.id],
      } as OutputData,
      draggable: false, selectable: false,
    });
  }

  const edges: Edge[] = [];
  for (const a of selected.agents) {
    edges.push({
      id: `e-entry-a-${a}`,
      source: ENTRY_NODE_ID, target: `a-${a}`,
      type: "smoothstep",
      animated: true,
      style: { stroke: "#C95C1A", strokeWidth: 1.4 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#C95C1A", width: 11, height: 11 },
    });
  }
  for (const a of selected.agents) {
    for (const o of selected.outputs) {
      edges.push({
        id: `e-a-${a}-o-${o}`,
        source: `a-${a}`, target: `o-${o}`,
        type: "smoothstep",
        animated: true,
        style: { stroke: "#3F7A55", strokeWidth: 1.2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#3F7A55", width: 10, height: 10 },
      });
    }
  }

  return { nodes, edges };
}

/* ─── Component ───────────────────────────────────────────────── */

export function AgenticContextFlow(): JSX.Element {
  const [audience, setAudience] = useState<Audience>("designer");
  const [selectedId, setSelectedId] = useState<string>("addItem");
  const [expanded, setExpanded] = useState(false);

  const visibleTriggers = useMemo(
    () => TRIGGERS.filter((t) => t.audience === audience || t.audience === "both"),
    [audience],
  );

  useEffect(() => {
    // If the audience switch hid the current selection, pick the first
    // visible trigger so the picture is never empty.
    if (!visibleTriggers.some((t) => t.id === selectedId)) {
      const first = visibleTriggers[0];
      if (first) setSelectedId(first.id);
    }
  }, [visibleTriggers, selectedId]);

  const selected = useMemo(() => visibleTriggers.find((t) => t.id === selectedId), [visibleTriggers, selectedId]);

  const { nodes, edges } = useMemo(() => layoutRight(selected), [selected]);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [h, setH] = useState(640);
  useEffect(() => {
    function r(): void {
      if (!wrapRef.current) return;
      const w = wrapRef.current.clientWidth;
      setH(Math.min(820, Math.max(560, Math.round(w / 1.55))));
    }
    r();
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setExpanded(false); }
    if (expanded) {
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }
    return () => { /* no-op */ };
  }, [expanded]);

  const flow = (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={NODE_TYPES}
      fitView
      fitViewOptions={{ padding: 0.28 }}
      minZoom={0.55}
      maxZoom={1.4}
      panOnDrag={false}
      zoomOnScroll={false}
      zoomOnPinch
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={22} size={1} color="#ece7dc" />
      {expanded ? <Controls showInteractive={false} className="!shadow-sm" /> : null}
    </ReactFlow>
  );

  const triggerColumn = (
    <ul className="space-y-1.5 pr-1 overflow-y-auto h-full">
      {visibleTriggers.map((t) => {
        const isSelected = t.id === selectedId;
        return (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => setSelectedId(t.id)}
              className={cls(
                "w-full rounded-xl border text-left transition-all px-3 py-2 flex items-start gap-2",
                isSelected
                  ? "bg-ink-950 text-cream border-ink-950 shadow-md"
                  : "bg-cream border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950",
              )}
            >
              <span className={cls("mt-0.5 shrink-0", isSelected ? "text-foyr" : "text-ink-500")}>{t.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[9.5px] uppercase tracking-wider font-mono opacity-70">
                  {t.kind === "action" ? "Action" : "Question"} · {t.audience === "client" ? "client" : "designer"}
                </div>
                <div className="text-[12.5px] font-semibold leading-tight mt-0.5">{t.label}</div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <section className="panel p-5 sm:p-7">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="eyebrow">What the agent does, in context</div>
          <div className="display-3 text-ink-950 mt-1 leading-tight">
            Click anything you (or your client) might do — see who reacts and what comes back.
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="inline-flex bg-cream-dark border border-border rounded-lg p-0.5">
            {(["designer", "client"] as const).map((a) => (
              <button
                key={a}
                onClick={() => setAudience(a)}
                className={cls(
                  "px-2.5 py-1 rounded-md text-[11px] font-medium transition",
                  audience === a ? "bg-ink-950 text-cream" : "text-ink-700 hover:text-ink-950",
                )}
              >
                {a === "designer" ? "Designer" : "Client"}
              </button>
            ))}
          </div>
          <button
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-cream-dark border border-border text-[11px] text-ink-700 hover:text-ink-950 transition"
          >
            <Maximize2 className="w-3 h-3" /> Expand
          </button>
        </div>
      </div>

      <div ref={wrapRef} className="grid grid-cols-[260px_1fr] gap-4" style={{ height: h }}>
        <div className="rounded-2xl border border-border/60 bg-cream/60 p-2 overflow-hidden">
          {triggerColumn}
        </div>
        <div className="rounded-2xl border border-border/60 overflow-hidden bg-[radial-gradient(circle_at_50%_50%,rgba(201,92,26,0.04),transparent_70%)]">
          {flow}
        </div>
      </div>

      {/* Strip under the canvas */}
      <div className="mt-3 grid grid-cols-3 gap-3 text-[10.5px] uppercase tracking-wider text-ink-500 font-mono">
        <div>1 · You / your client</div>
        <div className="text-center">2 · Specialists react</div>
        <div className="text-right">3 · What you see</div>
      </div>

      {expanded ? createPortal((
        <AnimatePresence>
          <motion.div
            key="bg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[80] bg-ink-950/40 backdrop-blur-sm"
            onClick={() => setExpanded(false)}
          />
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-4 sm:inset-8 z-[81] panel overflow-hidden flex flex-col"
          >
            <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border/60">
              <div>
                <div className="eyebrow">Agentic flow · expanded</div>
                <div className="display-3 text-ink-950 leading-tight">In context: {selected?.label}</div>
              </div>
              <button onClick={() => setExpanded(false)} aria-label="Close" className="w-8 h-8 rounded-lg bg-cream-dark border border-border grid place-items-center text-ink-700 hover:text-ink-950 transition">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="flex-1 grid grid-cols-[300px_1fr] gap-4 p-4 overflow-hidden">
              <div className="rounded-2xl border border-border/60 bg-cream/60 p-2 overflow-hidden">
                {triggerColumn}
              </div>
              <div className="rounded-2xl border border-border/60 overflow-hidden bg-[radial-gradient(circle_at_50%_50%,rgba(201,92,26,0.04),transparent_70%)]">
                {flow}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      ), document.body) : null}
    </section>
  );
}
